import {
  Component,
  HostListener,
  ElementRef,
  inject,
  computed,
  input,
  model,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { School } from '../../../types/school';
import { SchoolAuthorityDetail } from '../../../types/rest-api';
import {
  NavbarEntitySwitcherVariant,
  resolveSelectedEntityDisplayLabel,
  resolveSingleEntityDisplayName,
} from './navbar-entity-switcher.utils';

@Component({
  selector: 'app-navbar-entity-switcher',
  standalone: true,
  imports: [],
  templateUrl: './navbar-entity-switcher.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './navbar-entity-switcher.component.scss',
})
export class NavbarEntitySwitcherComponent {
  private elementRef = inject(ElementRef<HTMLElement>);

  readonly caretDownWhiteIconSrc = '/assets/icons/navbar/caret-down-white.svg';

  variant = input.required<NavbarEntitySwitcherVariant>();
  schools = input.required<School[]>();
  authorities = input.required<SchoolAuthorityDetail[]>();
  selectedEntityValue = input.required<string>();

  dropdownOpen = model(false);
  entitySelected = output<string>();

  entityCount = computed(
    () => this.schools().length + this.authorities().length
  );

  displayLabel = computed(() =>
    resolveSelectedEntityDisplayLabel(
      this.selectedEntityValue(),
      this.authorities(),
      this.schools()
    )
  );

  singleEntityName = computed(() =>
    resolveSingleEntityDisplayName(this.authorities(), this.schools())
  );

  triggerButtonClass = computed(() => {
    const base =
      'bn-entity-switcher relative z-0 w-full h-[42px] pl-4 cursor-pointer outline-none transition-colors truncate text-left';
    if (this.variant() === 'primary') {
      return `${base} pr-10 rounded-[10px] border border-white/25 bg-light-primary text-[16px] font-medium tracking-[-0.5px] text-white shadow-sm focus-visible:ring-2 focus-visible:ring-white/70`;
    }
    return `${base} pr-8 rounded-[28px] border border-light-outline-variant bg-bn-box text-[13px] font-semibold text-light-on-surface focus:border-light-primary focus:bg-light-surface`;
  });

  singleEntityClass = computed(() => {
    const base =
      'bn-entity-switcher__single relative z-0 w-full h-[42px] pl-4 pr-4 flex items-center truncate';
    if (this.variant() === 'primary') {
      return `${base} rounded-[10px] border border-white/25 bg-light-primary text-[16px] font-medium tracking-[-0.5px] text-white`;
    }
    return `${base} rounded-[28px] border border-light-outline-variant bg-bn-box text-[13px] font-semibold text-light-on-surface`;
  });

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.dropdownOpen.update(v => !v);
  }

  selectEntity(value: string): void {
    this.entitySelected.emit(value);
    this.dropdownOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.dropdownOpen.set(false);
    }
  }
}
